import { recoveryScope } from './checkoutIdentityRecovery';
import { checkoutAttemptContext } from './checkoutContext';
import { checkoutDispatchFailure } from './checkoutDispatchFailure';

const key = 'checkout-issuance-recovery-v1';
export const issuanceRecoveryMessage =
  'Payment recovery is required. Your existing checkout is kept. Check its status before paying; do not switch payment methods.';

// Includes changes that do not affect the start request, such as a tip or coupon.
export const issuanceIntent = (state) =>
  JSON.stringify([
    checkoutAttemptContext(state, state.address?.intentAnchor),
    state.order.checkoutData?.delivery_revision,
    state.order.checkoutData?.total,
    state.order.checkoutAdjustmentSelection,
    state.order.dynamicFeesValues,
    state.order.notes,
    state.order.phone,
  ]);

export function readCheckoutIssuance(state) {
  const stores = ['sessionStorage', 'localStorage'];
  for (let index = 0; index < stores.length; index += 1) {
    try {
      const record = JSON.parse(window[stores[index]].getItem(key));
      if (record?.scope === recoveryScope(state) && record.checkoutId)
        return record;
    } catch (_error) {
      // Storage may be blocked. Never manufacture missing request parameters.
    }
  }
  return null;
}

export function saveCheckoutIssuance(record) {
  const stores = ['sessionStorage', 'localStorage'];
  for (let index = 0; index < stores.length; index += 1) {
    try {
      window[stores[index]].setItem(key, JSON.stringify(record));
      return record;
    } catch (_error) {
      // Try independent storage before failing closed.
    }
  }
  throw new Error(
    'Payment recovery could not be saved. Allow browser storage and retry.'
  );
}

export function beginCheckoutIssuance(state, request, kind = 'embedded') {
  const fields =
    kind === 'embedded'
      ? ['checkoutId', 'clientId', 'tipAmount', 'expectedDeliveryRevision']
      : [
          'checkout',
          'client',
          'success_url',
          'cancel_url',
          'expected_delivery_revision',
        ];
  const envelope = Object.fromEntries(
    fields
      .filter((field) => Object.prototype.hasOwnProperty.call(request, field))
      .map((field) => [field, request[field]])
  );
  const summaryFields = [
    'id',
    'restaurant',
    'accountId',
    'delivery_revision',
    'total',
    'subtotal',
    'taxes',
    'fees',
    'coupons',
    'items',
    'payments',
    'created_at',
    'ttl_seconds',
  ];
  const checkout = state.order.checkoutData;
  const snapshot = Object.fromEntries(
    summaryFields
      .filter((field) => Object.prototype.hasOwnProperty.call(checkout, field))
      .map((field) => [field, checkout[field]])
  );
  if (Array.isArray(snapshot.payments))
    snapshot.payments = snapshot.payments.map(({ type }) => ({ type }));
  return saveCheckoutIssuance({
    scope: recoveryScope(state),
    checkoutId: state.order.checkoutData.id,
    client: state.restaurant?.selectedRestaurantId,
    kind,
    intent: issuanceIntent(state),
    snapshot: JSON.parse(JSON.stringify(snapshot)),
    // This envelope contains only API parameters, never returned credentials.
    request: ['cash', 'confirmation'].includes(kind)
      ? undefined
      : JSON.parse(JSON.stringify(envelope)),
  });
}

// Restoring an original local summary is not synchronization or payment readiness.
// Never replace another current ID, or restore the quote over changed intent.
export function recoverIssuanceSnapshot(state, record) {
  if (
    state.order.checkoutData?.id ||
    !record?.snapshot ||
    record.scope !== recoveryScope(state) ||
    record.snapshot.id !== record.checkoutId
  )
    return null;
  const restored = {
    ...state,
    order: { ...state.order, checkoutData: record.snapshot },
  };
  return issuanceIntent(restored) === record.intent ? record.snapshot : null;
}

export function clearCheckoutIssuance() {
  ['sessionStorage', 'localStorage'].forEach((name) => {
    try {
      window[name].removeItem(key);
    } catch (_error) {
      // A retained record fails closed on the next recovery.
    }
  });
}

export function retainIssuanceDiagnostic(state, error) {
  const record = readCheckoutIssuance(state);
  const diagnostic = checkoutDispatchFailure(error);
  if (record && diagnostic) saveCheckoutIssuance({ ...record, diagnostic });
}

export function canRetryCheckoutIssuance(record, resolution, state) {
  return (
    record?.kind === 'embedded' &&
    !!record.request &&
    record.scope === recoveryScope(state) &&
    record.checkoutId === state.order.checkoutData?.id &&
    record.request.checkoutId === record.checkoutId &&
    record.request.clientId === record.client &&
    record.intent === issuanceIntent(state) &&
    resolution?.checkout_id === record.checkoutId &&
    resolution.replacement_allowed === false &&
    !resolution.already_confirmed &&
    !resolution.operation_pending &&
    ['unavailable', 'unpaid', 'action_required'].includes(resolution.state)
  );
}
